/**
 * Counts down to a drop.
 *
 * The markup arrives with the server's own figures already filled in, so the
 * hero is correct in the first paint and this only keeps it moving. That
 * matters more than usual here: a countdown that renders empty and then
 * populates would resize the hero a second after load, which is exactly the
 * layout shift the homepage was fixed for.
 *
 * Pages are cached, so the server's numbers can be stale by the time someone
 * sees them. The first tick runs immediately rather than after a second,
 * which corrects any drift before it can be noticed.
 */
class DropCountdown extends HTMLElement {
  #timer = null;

  connectedCallback() {
    const iso = this.getAttribute('datetime');
    this.target = iso ? Date.parse(iso) : NaN;

    if (Number.isNaN(this.target)) {
      // No usable date: leave the server-rendered figures alone rather than
      // replacing them with zeroes.
      return;
    }

    this.parts = {
      days: this.querySelector('[data-unit="days"]'),
      hours: this.querySelector('[data-unit="hours"]'),
      minutes: this.querySelector('[data-unit="minutes"]'),
      seconds: this.querySelector('[data-unit="seconds"]'),
    };

    this.#tick();
    this.#timer = setInterval(() => this.#tick(), 1000);

    // A tab left open for hours drifts, and a phone that slept does not fire
    // intervals at all. Re-sync whenever the page becomes visible again.
    this.onVisible = () => {
      if (!document.hidden) this.#tick();
    };
    document.addEventListener('visibilitychange', this.onVisible);
  }

  disconnectedCallback() {
    if (this.#timer) clearInterval(this.#timer);
    document.removeEventListener('visibilitychange', this.onVisible);
  }

  #tick() {
    const remaining = Math.max(0, this.target - Date.now());

    if (remaining === 0) {
      this.setAttribute('elapsed', '');
      if (this.#timer) clearInterval(this.#timer);
    }

    const totalSeconds = Math.floor(remaining / 1000);
    // The first tick only corrects cached figures, so it doesn't animate.
    const roll =
      this.hasTicked && !document.hidden && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const set = (node, value) => {
      if (!node) return;
      const next = String(value).padStart(2, '0');
      // Writing an unchanged string still invalidates layout in some engines;
      // the seconds cell is the only one that usually differs.
      if (node.textContent === next) return;
      node.textContent = next;
      if (roll) {
        node.animate(
          [
            { opacity: 0, transform: 'translateY(-0.3em)' },
            { opacity: 1, transform: 'none' },
          ],
          { duration: 320, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
        );
      }
    };

    set(this.parts.days, Math.floor(totalSeconds / 86400));
    set(this.parts.hours, Math.floor((totalSeconds % 86400) / 3600));
    set(this.parts.minutes, Math.floor((totalSeconds % 3600) / 60));
    set(this.parts.seconds, totalSeconds % 60);
    this.hasTicked = true;
  }
}

if (!customElements.get('drop-countdown')) {
  customElements.define('drop-countdown', DropCountdown);
}
